import type {FC, ReactElement, ReactNode} from 'react'
import * as React from 'react'
import ReactMarkdown, {type Components} from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeSanitize, {defaultSchema} from 'rehype-sanitize'
import {Box, Link} from '@mui/material'
import type {Element} from 'hast'
import MarkdownTableGrid, {MarkdownTableSkeleton} from './MarkdownTableGrid'
import ChartBlock from '@containers/Dashboards/AgentChat/components/messages/charts/ChartBlock'
import {
  CONVERSATION_LINK_SCHEME,
  conversationIdFromHref,
  isConversationHref,
  useOpenConversation,
} from '@containers/Dashboards/AgentChat/hooks/useOpenConversation'

export interface KbRefTarget {
  documentId: string
  title?: string
  page?: number
}

interface Props {
  text: string
  onKbRefClick?: (ref: KbRefTarget) => void
  /** While streaming, hold a markdown table behind a stable skeleton until its block
   *  is complete, then render the DataGrid -- same render-on-complete behaviour as the
   *  chart widgets, so a half-arrived table can't shift the layout token-by-token. */
  streaming?: boolean
}

/** Extract visible plain text from heading children without using `any` */
function extractText(children: ReactNode): string {
  return React.Children.toArray(children)
    .map((c) => {
      if (typeof c === 'string') return c
      if (React.isValidElement(c)) {
        const inner = c.props?.children as ReactNode | undefined
        if (typeof inner === 'string') return inner
        if (Array.isArray(inner)) {
          return inner.filter((n) => typeof n === 'string').join('')
        }
      }
      return ''
    })
    .join('')
    .trim()
}

// Allow kbdoc: and alpinist-conversation: in links so rehype-sanitize doesn't strip them
const kbSanitizeSchema = {
  ...defaultSchema,
  protocols: {
    ...defaultSchema.protocols,
    href: [
      ...(((defaultSchema.protocols && defaultSchema.protocols.href) as string[]) || [
        'http',
        'https',
        'mailto',
        'tel',
      ]),
      'kbdoc',
      CONVERSATION_LINK_SCHEME,
    ],
  },
}

const MarkdownText: FC<Props> = ({text, onKbRefClick, streaming = false}) => {
  // remember the last seen heading (H1–H6)
  const lastHeadingRef = React.useRef<string>('')
  const openConversation = useOpenConversation()

  const mdComponents: Components = {
    h1: ({children}) => {
      lastHeadingRef.current = extractText(children) || lastHeadingRef.current
      return <h1>{children}</h1>
    },
    h2: ({children}) => {
      lastHeadingRef.current = extractText(children) || lastHeadingRef.current
      return <h2>{children}</h2>
    },
    h3: ({children}) => {
      lastHeadingRef.current = extractText(children) || lastHeadingRef.current
      return <h3>{children}</h3>
    },
    h4: ({children}) => {
      lastHeadingRef.current = extractText(children) || lastHeadingRef.current
      return <h4>{children}</h4>
    },
    h5: ({children}) => {
      lastHeadingRef.current = extractText(children) || lastHeadingRef.current
      return <h5>{children}</h5>
    },
    h6: ({children}) => {
      lastHeadingRef.current = extractText(children) || lastHeadingRef.current
      return <h6>{children}</h6>
    },

    // Custom link handling for kbdoc: references
    a: ({href, children}) => {
      // An earlier conversation Alpinist used (AB#6775): opens it in this chat.
      // Never a real href — the browser must not try to navigate to the scheme —
      // and a malformed id is just text.
      if (isConversationHref(href)) {
        const conversationId = conversationIdFromHref(href)
        if (!conversationId) return <span>{children}</span>
        return (
          <Link
            component="button"
            type="button"
            onClick={() => openConversation(conversationId)}
            data-testid="alpinist-conversation-link"
            sx={{
              font: 'inherit',
              verticalAlign: 'baseline',
              textAlign: 'left',
              textDecoration: 'underline',
              textUnderlineOffset: '2px',
              '&:hover': {textDecorationThickness: '2px'},
            }}
          >
            {children}
          </Link>
        )
      }

      if (href && href.startsWith('kbdoc:') && onKbRefClick) {
        // href format: kbdoc:{document_id}?page=XX&...
        const raw = href.slice('kbdoc:'.length)
        const [idPart, query = ''] = raw.split('?')
        let page: number | undefined

        if (query) {
          for (const kv of query.split('&')) {
            const [k, v] = kv.split('=')
            if (k === 'page' && v) {
              const parsed = Number(v)
              if (!Number.isNaN(parsed)) page = parsed
            }
          }
        }

        const title = extractText(children)

        const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
          e.preventDefault()
          onKbRefClick({documentId: idPart, page, title})
        }

        return (
          <a href={href} onClick={handleClick}>
            {children}
          </a>
        )
      }

      // Normal links: open in new tab
      return (
        <a href={href} target="_blank" rel="noreferrer">
          {children}
        </a>
      )
    },

    // A ```chart fenced block carries a JSON chart spec the agent emits. Render it
    // as a widget (ChartBlock parses + dispatches); non-chart code is left as-is.
    code: ({className, children}): ReactElement => {
      if (className && /\blanguage-chart\b/.test(className)) {
        return <ChartBlock raw={String(children)} streaming={streaming} />
      }
      return <code className={className}>{children}</code>
    },

    // The chart widget supplies its own container, so unwrap the styled <pre> box
    // that would otherwise wrap a ```chart block; ordinary code blocks keep it.
    pre: ({node, children}): ReactNode => {
      const child = node?.children?.[0] as Element | undefined
      const cls = child?.properties?.className
      const isChart = Array.isArray(cls) && cls.includes('language-chart')
      return isChart ? children : <pre>{children}</pre>
    },

    // Hold every markdown table behind a stable skeleton for the whole streaming turn,
    // and render the rich DataGrid only once the turn is final. The DataGrid mounts and
    // autosizes its columns on render, so letting it appear mid-stream (as soon as any
    // content follows the table) is exactly what shifts the layout while the rest of the
    // turn keeps arriving. A fixed-size skeleton until `streaming` clears keeps the
    // bubble stable -- the same render-on-complete contract the chart widgets follow.
    table: ({node}): ReactElement | null => {
      if (streaming) {
        return <MarkdownTableSkeleton />
      }
      return node ? (
        <MarkdownTableGrid node={node as Element} title={lastHeadingRef.current} />
      ) : null
    },
  }

  return (
    <Box
      sx={{
        overflow: 'visible',
        '& h1': {typography: 'h2', mt: 3, mb: 1.5},
        '& h2': {typography: 'h3', mt: 3, mb: 1.25},
        '& h3': {typography: 'h4', mt: 2, mb: 1},
        '& p, & li': {fontSize: '0.875rem', lineHeight: 1.4, mt: 1},
        '& p + p': {mt: 2},
        '& ul, & ol': {pl: 3, my: 0.5},
        '& li': {my: 0.25},
        '& a': {
          color: 'primary.main',
          textDecoration: 'underline',
          textUnderlineOffset: '2px',
          '&:hover': {textDecorationThickness: '2px'},
        },
        '& pre': {p: 1, borderRadius: 1, overflowX: 'auto', bgcolor: 'background.default'},
        '& code': {
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: '0.85em',
        },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[[rehypeSanitize, kbSanitizeSchema]]}
        components={mdComponents}
        // allow kbdoc: URIs to survive react-markdown's default filtering
        urlTransform={(href) => {
          if (!href) return href
          if (href.startsWith('kbdoc:')) return href
          return href
        }}
      >
        {text ?? ''}
      </ReactMarkdown>
    </Box>
  )
}

export default React.memo(MarkdownText, (a, b) => a.text === b.text && a.streaming === b.streaming)
